import { useState } from 'react';
import axios from 'axios';
import { uploadAsset } from '../../services/adminAssets';

interface Props {
  onCancel: () => void;
  onUploaded: () => void;
}

export default function SingleAssetUploadDialog({ onCancel, onUploaded }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [licenseId, setLicenseId] = useState('');
  const [licenseText, setLicenseText] = useState('');
  const [loopable, setLoopable] = useState(false);
  const [author, setAuthor] = useState('');
  const [sourceName, setSourceName] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicateInfo, setDuplicateInfo] = useState<string | null>(null);

  const isFont = !!file && /\.(ttf|otf)$/i.test(file.name);
  const isAudio = !!file && /\.(wav|ogg)$/i.test(file.name);

  const handleFile = (selected: File | null) => {
    setFile(selected);
    if (selected && !name.trim()) {
      const stem = selected.name.replace(/\.[^.]+$/, '');
      setName(stem);
    }
  };

  const canSubmit =
    !!file && !!name.trim() && !busy && (!isFont || !!licenseText.trim()) && (!isAudio || !!licenseId.trim());

  const handleSubmit = async () => {
    if (!file || !name.trim()) return;
    setBusy(true);
    setError(null);
    setDuplicateInfo(null);
    try {
      const result = await uploadAsset({
        file,
        name: name.trim(),
        description: description.trim() || undefined,
        assetType: isFont ? 'font' : isAudio ? 'audio' : 'image',
        visibility: 'Shared',
        tags: tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        licenseId: isFont || isAudio ? licenseId.trim() || undefined : undefined,
        licenseText: isFont ? licenseText.trim() || undefined : undefined,
        ...(isAudio && {
          loopable,
          author: author.trim() || undefined,
          sourceName: sourceName.trim() || undefined,
          sourceUrl: sourceUrl.trim() || undefined
        })
      });
      if (result.isExistingAsset) {
        setDuplicateInfo(`This file already exists in the library (asset id: ${result.assetId}).`);
        setBusy(false);
      } else {
        onUploaded();
      }
    } catch (err) {
      setError((axios.isAxiosError(err) && (err.response?.data?.error as string | undefined)) || 'Upload failed.');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onCancel}>
      <div
        className="w-full max-w-md rounded border border-neutral-800 bg-neutral-950 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold">Upload single asset</h3>

        <div className="mt-4">
          <label className="text-xs uppercase tracking-wide text-neutral-500">File (image, font or audio)</label>
          <input
            type="file"
            accept="image/*,.ttf,.otf,.wav,.ogg"
            onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
            className="mt-1 block w-full text-sm file:mr-3 file:rounded file:border file:border-neutral-700 file:bg-neutral-900 file:px-3 file:py-1.5 file:text-sm file:text-neutral-200 hover:file:bg-neutral-800"
          />
          {file && (
            <div className="mt-1 truncate text-xs text-neutral-500">
              {file.name} ({Math.round(file.size / 1024)} KB)
            </div>
          )}
        </div>

        <div className="mt-3">
          <label className="text-xs uppercase tracking-wide text-neutral-500">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded border border-neutral-800 bg-neutral-900 px-2 py-1.5 text-sm focus:border-neutral-600 focus:outline-none"
          />
        </div>

        <div className="mt-3">
          <label className="text-xs uppercase tracking-wide text-neutral-500">Description (optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="mt-1 w-full rounded border border-neutral-800 bg-neutral-900 px-2 py-1.5 text-sm focus:border-neutral-600 focus:outline-none"
          />
        </div>

        <div className="mt-3">
          <label className="text-xs uppercase tracking-wide text-neutral-500">Tags (comma-separated, optional)</label>
          <input
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="ui, button, blue"
            className="mt-1 w-full rounded border border-neutral-800 bg-neutral-900 px-2 py-1.5 text-sm focus:border-neutral-600 focus:outline-none"
          />
        </div>

        {isFont && (
          <>
            <div className="mt-3">
              <label className="text-xs uppercase tracking-wide text-neutral-500">License ID (e.g. OFL-1.1)</label>
              <input
                type="text"
                value={licenseId}
                onChange={(e) => setLicenseId(e.target.value)}
                placeholder="OFL-1.1"
                className="mt-1 w-full rounded border border-neutral-800 bg-neutral-900 px-2 py-1.5 text-sm focus:border-neutral-600 focus:outline-none"
              />
            </div>

            <div className="mt-3">
              <label className="text-xs uppercase tracking-wide text-neutral-500">License text (required for fonts)</label>
              <textarea
                value={licenseText}
                onChange={(e) => setLicenseText(e.target.value)}
                rows={4}
                placeholder="Paste the full license text — it ships alongside the font in built games."
                className="mt-1 w-full rounded border border-neutral-800 bg-neutral-900 px-2 py-1.5 text-sm focus:border-neutral-600 focus:outline-none"
              />
            </div>
          </>
        )}

        {isAudio && (
          <>
            <p className="mt-3 text-xs text-neutral-500">
              A .wav file is a sound effect and an .ogg file a music track. Shared audio must be CC0.
            </p>
            <div className="mt-3">
              <label className="text-xs uppercase tracking-wide text-neutral-500">License ID</label>
              <input
                type="text"
                value={licenseId}
                onChange={(e) => setLicenseId(e.target.value)}
                placeholder="CC0-1.0"
                className="mt-1 w-full rounded border border-neutral-800 bg-neutral-900 px-2 py-1.5 text-sm focus:border-neutral-600 focus:outline-none"
              />
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm text-neutral-300">
              <input
                type="checkbox"
                checked={loopable}
                onChange={(e) => setLoopable(e.target.checked)}
                className="h-4 w-4 accent-blue-500"
              />
              Loops seamlessly
            </label>
            <div className="mt-3">
              <label className="text-xs uppercase tracking-wide text-neutral-500">Author (optional)</label>
              <input
                type="text"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                className="mt-1 w-full rounded border border-neutral-800 bg-neutral-900 px-2 py-1.5 text-sm focus:border-neutral-600 focus:outline-none"
              />
            </div>
            <div className="mt-3">
              <label className="text-xs uppercase tracking-wide text-neutral-500">Source name (optional)</label>
              <input
                type="text"
                value={sourceName}
                onChange={(e) => setSourceName(e.target.value)}
                placeholder="Kenney"
                className="mt-1 w-full rounded border border-neutral-800 bg-neutral-900 px-2 py-1.5 text-sm focus:border-neutral-600 focus:outline-none"
              />
            </div>
            <div className="mt-3">
              <label className="text-xs uppercase tracking-wide text-neutral-500">Source URL (optional)</label>
              <input
                type="url"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                className="mt-1 w-full rounded border border-neutral-800 bg-neutral-900 px-2 py-1.5 text-sm focus:border-neutral-600 focus:outline-none"
              />
            </div>
          </>
        )}

        {duplicateInfo && (
          <div className="mt-3 rounded border border-yellow-800 bg-yellow-900/30 px-3 py-2 text-sm text-yellow-200">
            {duplicateInfo}
          </div>
        )}
        {error && <div className="mt-3 text-sm text-red-400">{error}</div>}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded border border-neutral-800 px-3 py-1.5 text-sm hover:bg-neutral-900"
          >
            {duplicateInfo ? 'Close' : 'Cancel'}
          </button>
          {!duplicateInfo && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="rounded border border-blue-700 bg-blue-700 px-3 py-1.5 text-sm text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              {busy ? 'Uploading…' : 'Upload'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
